/**
 * Pure helpers + React Query keys for the secret team chat in team games
 * (docs/team-games-plan.md §9 "Team chat"). One group thread per team; the
 * server decides membership with the same `teamChatTeam` used here.
 *
 * Kept free of react/react-native imports so the data hooks, the socket
 * reducer and the tests can all import it.
 */
import { isTeamGame, teamChatTeam, teamLabel, type TeamChatMemberResponse, type TeamChatResponse, type TeamChatSeat, type TeamChatState, type TeamId, type TeamMessageResponse } from '@warcarp/api-types'

/** Cache key for a game's team thread (drives the drawer badge too). */
export const teamChatKey = (gameId: string) => ['games', gameId, 'team-chat'] as const

/** In-app route of the team thread. */
export const teamChatRoute = (gameId: string) => `/(app)/games/${gameId}/dm/team`

/** Game statuses with a team chat (finished games keep it, read-only). */
const TEAM_CHAT_STATUSES = new Set(['ACTIVE', 'PAUSED', 'COMPLETE'])

/**
 * The viewer's team when they can use its chat in this game, else null:
 * free-for-all, lobbies, spectators, bots and resigned players get null, so
 * the app never asks the server for a thread it would refuse.
 */
export function viewerTeamChat(
  game: { status: string; state?: unknown; players?: readonly TeamChatSeat[] } | null | undefined,
  userId: string | null | undefined,
): TeamId | null {
  if (!game || !TEAM_CHAT_STATUSES.has(game.status)) return null
  return teamChatTeam(game.state as TeamChatState | null, game.players ?? [], userId)
}

/**
 * Append an incoming message to the cached thread, deduping by id (the WS
 * echo can race the POST response). Without a cached thread there is nothing
 * to append to: the next fetch brings it.
 */
export function appendTeamMessage(
  old: TeamChatResponse | undefined,
  incoming: TeamMessageResponse,
): TeamChatResponse | undefined {
  if (!old || old.team !== incoming.team) return old
  if (old.messages.some((m) => m.id === incoming.id)) return old
  return { ...old, messages: [...old.messages, incoming] }
}

/** Is this message unread for the viewer (from a teammate, after the last read)? */
function isUnread(m: TeamMessageResponse, lastReadAt: string | null, meId: string | undefined): boolean {
  if (m.fromUserId === meId) return false
  return lastReadAt === null || m.createdAt > lastReadAt
}

/**
 * Unread team messages for the viewer, derived from the cached thread so a
 * live message bumps the badge without a refetch. ISO timestamps from the
 * server compare correctly as strings.
 */
export function teamChatUnread(thread: TeamChatResponse | undefined, meId: string | undefined): number {
  if (!thread) return 0
  return thread.messages.filter((m) => isUnread(m, thread.lastReadAt, meId)).length
}

/** Index of the first unread message (the "NEW" divider), or -1. */
export function teamChatFirstUnreadIndex(thread: TeamChatResponse | undefined, meId: string | undefined): number {
  if (!thread) return -1
  return thread.messages.findIndex((m) => isUnread(m, thread.lastReadAt, meId))
}

/**
 * Optimistic read stamp: the newest message's time, so everything on screen
 * reads as seen without trusting the device clock. The server's own stamp
 * replaces it when the request returns.
 */
export function markTeamChatReadLocally(old: TeamChatResponse | undefined): TeamChatResponse | undefined {
  if (!old) return old
  const newest = old.messages.at(-1)?.createdAt
  if (!newest || (old.lastReadAt !== null && old.lastReadAt >= newest)) return old
  return { ...old, lastReadAt: newest, unreadCount: 0 }
}

/** "Team A · secret". */
export function teamChatTitle(team: TeamId): string {
  return `${teamLabel(team)} · secret`
}

/** "Ace, Dex and you": the header's member line, the viewer last. */
export function teamChatMemberLine(members: readonly TeamChatMemberResponse[], meId: string | undefined): string {
  const others = members.filter((m) => m.userId !== meId).map((m) => m.displayName)
  const names = members.some((m) => m.userId === meId) ? [...others, 'you'] : others
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`
}

/**
 * Thread rows for the shared MessageThread: each message carries its
 * sender's name and player colour (several people talk in a team thread).
 * `colorOf` maps a member to a hex colour; senders no longer in the chat
 * (a resigned teammate) keep their name with the neutral avatar.
 */
export function teamThreadMessages(
  thread: TeamChatResponse | undefined,
  colorOf: (member: TeamChatMemberResponse) => string,
): Array<{ id: string; authorId: string; authorName: string; authorColor?: string; content: string; createdAt: string; reactions: [] }> {
  if (!thread) return []
  const members = new Map(thread.members.map((m) => [m.userId, m]))
  return thread.messages.map((m) => {
    const member = members.get(m.fromUserId)
    return {
      id: m.id,
      authorId: m.fromUserId,
      authorName: member?.displayName ?? m.fromDisplayName,
      ...(member ? { authorColor: colorOf(member) } : {}),
      content: m.content,
      createdAt: m.createdAt,
      reactions: [],
    }
  })
}

/**
 * Where a 1:1 secret message link should go instead, or null to open it.
 * Team games have no 1:1 secret messages (the team chat is the only secret
 * channel), so a stale link or push goes to the viewer's team thread, or to
 * the game when they have none. Free-for-all (and an unloaded game) opens
 * the thread as before.
 */
export function dmRedirectForTeamGame(
  game: { id: string; status: string; state?: unknown; setupConfig?: unknown; players?: readonly TeamChatSeat[] } | null | undefined,
  userId: string | null | undefined,
): string | null {
  // The server's check (routes/directMessages.ts): a lobby set up with teams
  // already refuses 1:1 messages, before `state.teams` exists.
  if (!game || !isTeamGame(game)) return null
  return viewerTeamChat(game, userId) ? teamChatRoute(game.id) : `/(app)/games/${game.id}`
}
